/*
 * Tilvalg (add-ons) — se snippets/product-addons.liquid for markup og
 * dokumentation af metafelterne.
 *
 * Filen indlæses globalt fra layout/theme.liquid, ikke fra sektionen. Grunden
 * er kollektionssidens "Vælg muligheder"-popup: BulkModal henter produktets
 * bulk-quick-order-list-sektion og indsætter den med innerHTML, og scripts i
 * indsat HTML kører ikke. Er elementet allerede defineret, opgraderes den
 * indsatte <product-addons> af sig selv.
 */
if (!customElements.get('product-addons')) {
  customElements.define(
    'product-addons',
    class ProductAddons extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.inputs = Array.from(this.querySelectorAll('.product-addons__input'));
        this.status = this.querySelector('.product-addons__status');
        this.productId = parseInt(this.dataset.productId, 10);
        this.busy = false;
        // Varianter hvor kunden selv har skrevet et antal. Derefter følger
        // tilvalget ikke længere hovedvarens antal.
        this.manual = new Set();

        this.inputs.forEach((input) => {
          const row = input.closest('.product-addons__item');
          const quantityField = row.querySelector('.product-addons__qty');

          input.addEventListener('change', () => {
            // Fravalg nulstiller et manuelt antal, så tilvalget følger
            // hovedvaren igen hvis kunden sætter fluebenet på ny.
            if (!input.checked) this.manual.delete(this.variantIdOf(input));
            this.toggleQuantityField(input);
            this.sync();
          });

          if (!quantityField) return;

          quantityField.addEventListener('change', () => {
            const value = Math.max(1, parseInt(quantityField.value, 10) || 1);
            quantityField.value = value;
            this.manual.add(this.variantIdOf(input));
            this.sync();
          });
        });

        this.listenForCartChanges();
        this.sync(true);
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      variantIdOf(input) {
        return parseInt(input.dataset.variantId, 10);
      }

      quantityFieldOf(input) {
        return input.closest('.product-addons__item').querySelector('.product-addons__qty');
      }

      toggleQuantityField(input) {
        const wrapper = input.closest('.product-addons__item').querySelector('.product-addons__qty-wrapper');
        if (wrapper) wrapper.hidden = !input.checked;
      }

      /*
       * Ændrer kunden antallet i varianttabellen, skal tilvalgenes antal følge
       * med. Vi lytter to steder, fordi pubsub-scriptet (defer) ikke
       * nødvendigvis er indlæst når elementet opgraderes:
       * 1) temaets cartUpdate-event når det er tilgængeligt,
       * 2) direkte på tabellens antalsfelter som sikkerhedsnet.
       */
      listenForCartChanges() {
        const trySubscribe = () => {
          if (this.unsubscribe) return;
          if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.queueSync());
        };

        trySubscribe();
        window.addEventListener('load', trySubscribe);

        document.addEventListener('change', (event) => {
          const input = event.target;
          if (!input.matches || !input.matches('input[type="number"]')) return;
          if (input.classList.contains('product-addons__qty')) return;

          // På en kollektionsside kan flere produkters tabeller ligge i DOM'en
          // samtidig — reagér kun på vores eget produkt.
          const list = input.closest('quick-order-list');
          if (!list || parseInt(list.dataset.productId, 10) !== this.productId) return;

          this.queueSync();
        });
      }

      queueSync() {
        clearTimeout(this.syncTimer);
        // Vent til tabellens eget kurv-kald er færdigt, ellers læser vi et
        // forældet antal.
        this.syncTimer = setTimeout(() => this.sync(), 900);
      }

      async sync(initial) {
        if (this.busy) {
          this.queueSync();
          return;
        }
        this.busy = true;
        this.setStatus('');

        try {
          let cart = await this.getCart();

          // Ved første indlæsning afspejler checkboxene hvad der allerede
          // ligger i kurven, så kunden ikke mister sit valg ved refresh.
          if (initial) {
            this.inputs.forEach((input) => {
              const variantId = this.variantIdOf(input);
              const line = cart.items.find((item) => item.variant_id === variantId);
              input.checked = !!line;
              if (line && line.properties && line.properties._manuelt_antal === 'true') {
                this.manual.add(variantId);
              }
              this.toggleQuantityField(input);
            });
          }

          const mainQuantity = cart.items
            .filter((item) => item.product_id === this.productId)
            .reduce((total, item) => total + item.quantity, 0);

          for (const input of this.inputs) {
            const variantId = this.variantIdOf(input);
            const line = cart.items.find((item) => item.variant_id === variantId);
            const quantityField = this.quantityFieldOf(input);

            // Kun mens fluebenet står — er det lige fjernet, må et gammelt manuelt
            // antal ikke hentes tilbage fra den linje vi er ved at slette.
            if (input.checked && line && line.properties && line.properties._manuelt_antal === 'true') {
              this.manual.add(variantId);
            }

            const isManual = this.manual.has(variantId);

            // Standardantallet er hovedvarens antal. Har kunden selv skrevet
            // et tal, gælder det i stedet.
            const fallback = mainQuantity;

            if (quantityField) {
              if (isManual) {
                // Kun ved første indlæsning henter vi tallet fra kurven (kunden
                // kan have rettet det på kurvsiden). Derefter er feltet selv
                // facit — ellers ville kundens nye tal blive overskrevet af
                // det gamle, fordi kurven først opdateres nedenfor.
                if (initial && line) quantityField.value = line.quantity;
              } else if (fallback > 0) {
                quantityField.value = fallback;
              }
            }

            let wanted = 0;
            if (input.checked && mainQuantity > 0) {
              wanted = isManual && quantityField ? Math.max(1, parseInt(quantityField.value, 10) || 1) : fallback;
            }

            const properties = {
              'Tilvalg til': this.dataset.productTitle,
              // Understreg = skjult for kunde og ordre. Kurvsiden bruger den
              // til at vide om antallet skal følge hovedvaren.
              _manuelt_antal: isManual ? 'true' : 'false',
            };

            const propertiesMatch =
              line && line.properties && line.properties._manuelt_antal === properties._manuelt_antal;

            if (line && line.quantity === wanted && propertiesMatch) continue;

            if (line && wanted === 0) {
              cart = await this.postJSON(this.routes.cart_change_url, { id: line.key, quantity: 0 });
            } else if (line) {
              cart = await this.postJSON(this.routes.cart_change_url, {
                id: line.key,
                quantity: wanted,
                properties: properties,
              });
            } else if (wanted > 0) {
              await this.postJSON(this.routes.cart_add_url, {
                items: [{ id: variantId, quantity: wanted, properties: properties }],
              });
              cart = await this.getCart();
            }
          }

          // Er hovedproduktet ude af kurven, ryger fluebenene også.
          if (mainQuantity === 0) {
            this.inputs.forEach((input) => {
              input.checked = false;
              this.manual.delete(this.variantIdOf(input));
              this.toggleQuantityField(input);
            });
          }

          await this.refreshCartIcon();
        } catch (error) {
          console.error('Tilvalg kunne ikke opdateres:', error);
          this.setStatus('Tilvalget kunne ikke opdateres. Prøv igen.');
        } finally {
          this.busy = false;
        }
      }

      /*
       * window.routes er ikke nødvendigvis defineret når custom-elementet
       * opgraderes, så vi slår den op ved hvert kald og falder tilbage på
       * Shopifys standard-stier.
       */
      get routes() {
        return Object.assign(
          { cart_url: '/cart', cart_add_url: '/cart/add', cart_change_url: '/cart/change' },
          window.routes || {}
        );
      }

      getCart() {
        return fetch(this.routes.cart_url + '.js', { cache: 'no-store' }).then((response) => response.json());
      }

      postJSON(url, body) {
        return fetch(url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        }).then((response) => {
          if (!response.ok) throw new Error('Kurv-kaldet fejlede: ' + url);
          return response.json();
        });
      }

      refreshCartIcon() {
        const bubble = document.getElementById('cart-icon-bubble');
        if (!bubble) return Promise.resolve();

        return fetch(window.location.pathname + '?sections=cart-icon-bubble')
          .then((response) => response.json())
          .then((sections) => {
            const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
            // Sektionssvaret er pakket ind i #shopify-section-cart-icon-bubble,
            // hvis børn svarer til indholdet af <a id="cart-icon-bubble">.
            const updated =
              html.getElementById('shopify-section-cart-icon-bubble') || html.getElementById('cart-icon-bubble');
            if (updated) bubble.innerHTML = updated.innerHTML;
          })
          .catch(() => {});
      }

      setStatus(message) {
        if (this.status) this.status.textContent = message;
      }
    }
  );
}
